import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  History, 
  RotateCcw, 
  Clock, 
  User,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2
} from "lucide-react";
import PasswordVerifyDialog from "./PasswordVerifyDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromptHistoryDialog = ({ open, onClose, prompt, onRollback }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/prompts/${prompt.id}/history`,
        { withCredentials: true }
      );
      setHistory(response.data.history);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to fetch version history");
      setLoading(false);
    }
  };

  const handleRollbackClick = (version) => {
    setSelectedVersion(version);
    setShowPasswordDialog(true);
  };

  const handlePasswordVerified = async () => {
    setShowPasswordDialog(false);
    
    if (!selectedVersion) return;
    
    setRolling(true);
    
    try {
      const password = sessionStorage.getItem('verified_password');
      const response = await axios.post(
        `${API}/admin/prompts/${prompt.id}/rollback`,
        {
          password,
          version_number: selectedVersion.version_number
        },
        { withCredentials: true }
      );
      
      toast.success(`Rolled back to version ${selectedVersion.version_number}`);
      onRollback();
      onClose();
    } catch (error) {
      console.error("Rollback error:", error);
      toast.error(error.response?.data?.detail || "Failed to rollback");
    } finally {
      setRolling(false);
    }
  };

  const renderChangeIndicator = (changes, field) => {
    if (!changes || !changes[field]) return null;
    
    const change = changes[field];
    const oldVal = change.old;
    const newVal = change.new;
    
    if (newVal > oldVal) {
      return <TrendingUp className="w-3 h-3 text-green-600" />;
    } else if (newVal < oldVal) {
      return <TrendingDown className="w-3 h-3 text-red-600" />;
    } else {
      return <Minus className="w-3 h-3 text-slate-400" />;
    }
  };

  const renderChangeDetails = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <p className="text-xs text-slate-500">No changes</p>;
    }
    
    return (
      <div className="space-y-2">
        {Object.entries(changes).map(([field, change]) => (
          <div key={field} className="flex items-start gap-2 text-xs">
            <span className="font-medium text-slate-700 capitalize min-w-[120px]">
              {field.replace('_', ' ')}:
            </span>
            <div className="flex items-center gap-2">
              <span className="text-red-600 line-through">{JSON.stringify(change.old)}</span>
              <span className="text-slate-400">→</span>
              <span className="text-green-600 font-medium">{JSON.stringify(change.new)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <History className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <DialogTitle>Version History</DialogTitle>
                <DialogDescription>
                  {prompt.category.replace('_', ' ')} - View and rollback to previous versions
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
                <p className="mt-4 text-sm text-slate-600">Loading history...</p>
              </div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No version history available</p>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {history.map((version, index) => {
                const isLatest = index === 0;
                const isCurrent = version.version_number === prompt.version_number;
                
                return (
                  <Card 
                    key={version.id}
                    className={`${
                      isCurrent 
                        ? 'border-indigo-200 bg-indigo-50' 
                        : 'border-slate-200'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isCurrent 
                              ? 'bg-indigo-600 text-white' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            <span className="font-bold text-sm">v{version.version_number}</span>
                          </div>
                          
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">
                                Version {version.version_number}
                              </h4>
                              {isCurrent && (
                                <Badge variant="default" className="bg-indigo-600">
                                  Current
                                </Badge>
                              )}
                              {isLatest && !isCurrent && (
                                <Badge variant="secondary">
                                  Latest
                                </Badge>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(version.created_at).toLocaleString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                Admin
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {!isCurrent && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRollbackClick(version)}
                            disabled={rolling}
                          >
                            <RotateCcw className="w-3 h-3 mr-2" />
                            Rollback
                          </Button>
                        )}
                      </div>

                      {/* Change Reason */}
                      {version.change_reason && (
                        <div className="mb-3 p-2 bg-slate-50 rounded border border-slate-200">
                          <p className="text-xs font-medium text-slate-700 mb-1">Reason for Change:</p>
                          <p className="text-xs text-slate-600 italic">{version.change_reason}</p>
                        </div>
                      )}

                      {/* Changes Summary */}
                      {version.changes_summary && Object.keys(version.changes_summary).length > 0 && (
                        <div className="border-t border-slate-200 pt-3">
                          <p className="text-xs font-medium text-slate-700 mb-2">Changes:</p>
                          {renderChangeDetails(version.changes_summary)}
                        </div>
                      )}

                      {/* Current Features */}
                      <div className="border-t border-slate-200 pt-3 mt-3">
                        <p className="text-xs font-medium text-slate-700 mb-2">Settings in this version:</p>
                        <div className="grid grid-cols-4 gap-3">
                          <div className="bg-white p-2 rounded border border-slate-200">
                            <p className="text-xs text-slate-500">Temperature</p>
                            <p className="text-sm font-semibold text-slate-900">{version.features.temperature}</p>
                          </div>
                          <div className="bg-white p-2 rounded border border-slate-200">
                            <p className="text-xs text-slate-500">Max Tokens</p>
                            <p className="text-sm font-semibold text-slate-900">{version.features.max_tokens}</p>
                          </div>
                          <div className="bg-white p-2 rounded border border-slate-200">
                            <p className="text-xs text-slate-500">Style</p>
                            <p className="text-sm font-semibold text-slate-900 capitalize">{version.features.response_style}</p>
                          </div>
                          <div className="bg-white p-2 rounded border border-slate-200">
                            <p className="text-xs text-slate-500">Confidence</p>
                            <p className="text-sm font-semibold text-slate-900">
                              {(version.features.confidence_threshold * 100).toFixed(0)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {showPasswordDialog && (
        <PasswordVerifyDialog
          open={showPasswordDialog}
          onClose={() => setShowPasswordDialog(false)}
          onVerified={handlePasswordVerified}
        />
      )}
    </>
  );
};

export default PromptHistoryDialog;
